import Link from "next/link";
import Image from "next/image";
import { ImageWipe } from "@/components/motion";

/**
 * Condensed journal card (journal redesign, July 2026).
 *
 * Text-forward: the fine gold top rule and category tag carry the card,
 * and the title and standfirst both clamp to two lines so every card in a
 * row aligns. No crest elements here per the motif rules.
 *
 * `image` is optional. The homepage row passes one (restored by review,
 * July 2026); the journal index stays text-only so 29 cards do not become
 * a wall of thumbnails. Without an image the card renders exactly as the
 * condensed original.
 *
 * `href` overrides the default `/blog/<slug>` target, and `draft` badges the
 * card. Both exist for the local-only draft preview: unpublished drafts live
 * at `/drafts/<slug>`, and a card pointing at them must say so rather than
 * look like a published article.
 */
export function JournalCard({
  slug,
  category,
  title,
  excerpt,
  readingLabel,
  image,
  imageAlt,
  href,
  draft = false,
}: {
  slug: string;
  category: string;
  title: string;
  excerpt: string;
  readingLabel: string;
  image?: string;
  imageAlt?: string;
  href?: string;
  draft?: boolean;
}) {
  return (
    <Link
      href={href ?? `/blog/${slug}`}
      className="group flex h-full flex-col"
    >
      {image && (
        <ImageWipe className="relative mb-5 aspect-[16/10] w-full bg-oat">
          <Image
            src={image}
            // Server-resolved by getJournalImageAlt so the imagery manifest
            // stays out of this client bundle. The title fallback keeps the
            // image described even if a call site forgets to pass one.
            alt={imageAlt ?? title}
            fill
            className="object-cover silk-card-img"
            sizes="(max-width: 768px) 80vw, 340px"
          />
        </ImageWipe>
      )}
      <div className="flex flex-1 flex-col border-t border-gold pt-5">
      <p className="silk-eyebrow flex flex-wrap items-center gap-x-2.5">
        <span className="silk-eyebrow-rule" style={{ width: "1.25rem" }} />
        <span>{category}</span>
        {draft && (
          <>
            <span className="silk-eyebrow-rule" style={{ width: "0.75rem" }} />
            <span className="text-gold">Draft</span>
          </>
        )}
      </p>

      <h3 className="mt-4 silk-heading-4 line-clamp-2 text-ink transition-colors group-hover:text-gold-deep">
        {title}
      </h3>

      <p className="mt-3 silk-body-sm line-clamp-2 text-ink/65">{excerpt}</p>

      {/* Meta and CTA pinned to the card foot so rows align */}
      <p className="silk-caption mt-auto pt-5 text-muted">{readingLabel}</p>

      <span className="silk-cta mt-3 inline-flex text-[11px]">
        <span>{draft ? "Read the draft" : "Continue reading"}</span>
        <svg
          className="silk-cta-arrow"
          width="20"
          height="10"
          viewBox="0 0 22 10"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M0 5h20M16 1l4 4-4 4"
            stroke="currentColor"
            strokeWidth="1"
            strokeLinecap="square"
          />
        </svg>
      </span>
      </div>
    </Link>
  );
}
